import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Copy, FileSpreadsheet } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import {
  Button, PageShell, Pagination, Skeleton, toast,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { useProductFiltersStore } from '@/stores/useProductFiltersStore';
import { useProductColumnPrefs } from '@/stores/useProductColumnPrefs';
import { ProductTable } from '../components/ProductTable';
import { ProductTableToolbar } from '../components/ProductTableToolbar';
import { ProductColumnSettings } from '../components/ProductColumnSettings';
import { ProductBulkActionFooter } from '../components/ProductBulkActionFooter';
import { ProductBulkUpdateModal } from '../components/ProductBulkUpdateModal';
import { ProductExcelDialog } from '../components/ProductExcelDialog';
import { useProductsQuery, type ProductsQueryParams } from '../hooks/useProductsQuery';
import { useCopyProductMutation } from '../hooks/useCopyProductMutation';
import { useDeleteProductMutation } from '../hooks/useProductMutation';
import { useProductBulkMutation, type BulkUpdateInput } from '../hooks/useProductBulkMutation';
import { useBulkHomeSections, type BulkHomeSectionChanges } from '../hooks/useBulkHomeSections';
import { useHomeSectionsQuery } from '@/features/cms/mainPage/hooks/useHomeSectionsQuery';

/** 목록을 떠날 때 검색 상태를 유지할 곳 — 상품 상세·등록에서 돌아오면 보던 목록 그대로. */
const PRODUCT_SUBMENUS = new Set(['categories', 'exports', 'notice-spec-templates', 'overseas-shipping', 'shipping-fee', 'quantity-discount']);
export const keepsProductListState = (pathname: string): boolean => {
  const m = /^\/products(?:\/([^/]+))?\/?$/.exec(pathname);
  if (!m) return false;
  return m[1] === undefined || !PRODUCT_SUBMENUS.has(m[1]);
};

/**
 * QA-333: 검색 상태는 메모리 스토어라 다른 메뉴(카테고리 관리 등)에 다녀와도 그대로 남았다. 목록을 떠날 때
 * 상품 상세·등록이 아니면 초기화한다(언마운트 시점엔 라우터가 이미 이동해 window.location 이 도착지다).
 */
export const useResetProductFiltersOnLeave = () => {
  const resetFilters = useProductFiltersStore((s) => s.reset);
  useEffect(
    () => () => {
      if (!keepsProductListState(window.location.pathname)) resetFilters();
    },
    [resetFilters],
  );
};

export const ProductListPage = () => {
  const { t } = useTranslation('products');

  const keyword = useProductFiltersStore((s) => s.keyword);
  const saleStatuses = useProductFiltersStore((s) => s.saleStatuses);
  const resetFilters = useProductFiltersStore((s) => s.reset);
  const productType = useProductFiltersStore((s) => s.productType);
  const categoryId = useProductFiltersStore((s) => s.categoryId);
  const priceMin = useProductFiltersStore((s) => s.priceMin);
  const priceMax = useProductFiltersStore((s) => s.priceMax);
  const stockMin = useProductFiltersStore((s) => s.stockMin);
  const stockMax = useProductFiltersStore((s) => s.stockMax);
  const createdFrom = useProductFiltersStore((s) => s.createdFrom);
  const createdTo = useProductFiltersStore((s) => s.createdTo);
  const updatedFrom = useProductFiltersStore((s) => s.updatedFrom);
  const updatedTo = useProductFiltersStore((s) => s.updatedTo);
  const artistId = useProductFiltersStore((s) => s.artistId);
  const hasArtist = useProductFiltersStore((s) => s.hasArtist);
  const displayed = useProductFiltersStore((s) => s.displayed);
  const productCode = useProductFiltersStore((s) => s.productCode);
  const barcode = useProductFiltersStore((s) => s.barcode);
  const selfProductCode = useProductFiltersStore((s) => s.selfProductCode);
  const page = useProductFiltersStore((s) => s.page);
  const size = useProductFiltersStore((s) => s.size);
  const sort = useProductFiltersStore((s) => s.sort);
  const setSort = useProductFiltersStore((s) => s.setSort);
  const setPage = useProductFiltersStore((s) => s.setPage);
  const selectedIds = useProductFiltersStore((s) => s.selectedIds);
  const toggleSelected = useProductFiltersStore((s) => s.toggleSelected);
  const setSelected = useProductFiltersStore((s) => s.setSelected);
  const clearSelection = useProductFiltersStore((s) => s.clearSelection);
  useResetProductFiltersOnLeave();
  // ID89 노출 항목 설정 — 운영자가 켠 컬럼만 목록에 표시(클라 환경설정).
  const visibleColumns = useProductColumnPrefs((s) => s.visibleColumns);

  // 목록 조회 + 엑셀 export 가 같은 필터를 공유하도록 단일 params 로 구성.
  const queryParams: ProductsQueryParams = {
    page,
    size,
    sort,
    keyword,
    saleStatuses,
    productType,
    categoryId,
    priceMin,
    priceMax,
    stockMin,
    stockMax,
    createdFrom,
    createdTo,
    updatedFrom,
    updatedTo,
    artistId,
    hasArtist,
    displayed,
    productCode,
    barcode,
    selfProductCode,
  };
  const query = useProductsQuery(queryParams);

  const totalItems = query.data?.meta.pagination?.totalItems ?? 0;
  const items = query.data?.items ?? [];

  // ID60/QA-88 상품 복사 — 행 액션 또는 top-bar 버튼으로 복사.
  // 복사본은 BE 가 [복사본] prefix + status=DRAFT + displayed=false 로 즉시 생성한다.
  // ★QA-88 버그수정: 복사 직후 수정화면으로 navigate 하던 동작을 제거한다. 사용자가 그
  // 수정화면에서 저장하지 않고 취소/뒤로 가면 이미 생성된 DRAFT 가 목록에 남아 "저장 안 했는데
  // 복사본이 생성됨"으로 보였다. 복사는 "목록에 바로 생성, 수정은 따로"(QA 요구)가 정상 동작이므로,
  // 복사 후에는 목록에 머무르며 토스트로 안내하고 캐시를 갱신한다(수정은 사용자가 별도로 진입).
  const copy = useCopyProductMutation();
  // ID60 복사는 단일 대상 의미라 "정확히 1건 선택" 일 때만 활성.
  const copyTargetId = selectedIds.length === 1 ? selectedIds[0]! : null;

  const handleCopy = (productId: string) => {
    if (copy.isPending) return;
    copy.mutate(productId, {
      onSuccess: () => {
        clearSelection();
        toast.success(
          t('products.copy.created', '복사본을 목록에 생성했습니다. (미노출 임시저장 — 수정은 목록에서 진행)'),
        );
      },
      onError: (error) => {
        toast.error(friendlyMessage(error, t('products.copy.error', '상품 복사에 실패했습니다.')));
      },
    });
  };

  // ID90 일괄변경/엑셀 — be-bulk 엔드포인트(bulk-update/import) 호출.
  const { bulkUpdate, importExcel, importExcelFull } = useProductBulkMutation();
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [confirmBulkCopy, setConfirmBulkCopy] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  // QA-589 일괄 변경의 [메인 섹션 노출] — 사용 중 섹션(메인 노출 순서)과 넣기/빼기.
  const homeSectionsQuery = useHomeSectionsQuery();
  const activeHomeSections = [...(homeSectionsQuery.data ?? [])].filter((h) => h.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  const applyBulkHomeSections = useBulkHomeSections();
  const [sectionsBusy, setSectionsBusy] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false);

  // 부분 성공 결과를 토스트로 안내(succeeded/skipped/failed 집계).
  const reportResult = (
    res: { succeeded: number; skipped: number; failed: ReadonlyArray<unknown> },
    successKey: string,
  ) => {
    if (res.failed.length > 0) {
      toast.error(
        t('products.bulk.partialError', {
          failed: res.failed.length,
          succeeded: res.succeeded,
          defaultValue: `${res.succeeded}건 성공, ${res.failed.length}건 실패했습니다.`,
        }),
      );
    } else {
      toast.success(t(successKey, { count: res.succeeded, defaultValue: `${res.succeeded}건을 처리했습니다.` }));
    }
  };

  // ID88 일괄삭제 (Lead 결정 b) — orders 일괄취소와 동일 패턴: 선택건마다 단건 DELETE
  // (/admin/v1/products/{id}, soft-archive, 멱등) 순차 호출 + per-item try/catch + invalidate.
  // 신규 bulk 엔드포인트 대신 검증된 팀 패턴 재사용(BE-0, 회귀위험0, 주변 코드 일치).
  const del = useDeleteProductMutation();
  const [bulkBusy, setBulkBusy] = useState(false);

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkBusy(true);
    let failed = 0;
    for (const productId of ids) {
      try {
        await del.mutateAsync(productId);
      } catch {
        // 단건 실패는 집계 후 토스트 안내 — 나머지 삭제는 계속 진행(부분 성공 허용).
        failed += 1;
      }
    }
    setBulkBusy(false);
    setConfirmBulkDelete(false);
    clearSelection();
    if (failed > 0) {
      toast.error(
        t('products.bulk.partialError', {
          failed,
          succeeded: ids.length - failed,
          defaultValue: `${ids.length - failed}건 성공, ${failed}건 실패했습니다.`,
        }),
      );
    } else {
      toast.success(
        t('products.bulk.deleteSuccess', { count: ids.length, defaultValue: `${ids.length}건을 삭제했습니다.` }),
      );
    }
  };

  // QA-88 일괄복사 — 일괄삭제와 동일 패턴: 선택건마다 단건 copy(POST /products/{id}/copy)를
  // 순차 호출. 복사본은 BE 가 [복사본] prefix + DRAFT + 미노출로 즉시 생성 → 목록에 바로 보인다
  // ("목록에 바로 생성, 수정은 따로" QA 요구). 신규 bulk 엔드포인트 없이 검증된 루프 재사용(회귀 0).
  const handleBulkCopy = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkBusy(true);
    let failed = 0;
    for (const productId of ids) {
      try {
        await copy.mutateAsync(productId);
      } catch {
        // 단건 실패는 집계 후 안내 — 나머지 복사는 계속 진행(부분 성공 허용).
        failed += 1;
      }
    }
    setBulkBusy(false);
    setConfirmBulkCopy(false);
    clearSelection();
    if (failed > 0) {
      toast.error(
        t('products.bulk.partialError', {
          failed,
          succeeded: ids.length - failed,
          defaultValue: `${ids.length - failed}건 성공, ${failed}건 실패했습니다.`,
        }),
      );
    } else {
      toast.success(
        t('products.bulk.copySuccess', {
          count: ids.length,
          defaultValue: `복사본 ${ids.length}건을 목록에 생성했습니다. (미노출 임시저장)`,
        }),
      );
    }
  };

  // ID90 일괄변경 — 모달에서 받은 변경 필드 + 선택 id 로 bulk-update. QA-589 메인 섹션 넣기/빼기는 홈 섹션 API 로.
  const handleBulkUpdate = async (changes: Omit<BulkUpdateInput, 'productIds'>, sectionChanges: BulkHomeSectionChanges) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (Object.keys(sectionChanges).length > 0) {
      setSectionsBusy(true);
      try {
        const failed = await applyBulkHomeSections(activeHomeSections, ids, sectionChanges);
        if (failed.length > 0) toast.error(`메인 섹션 노출을 반영하지 못했습니다(${failed.join(', ')}).`);
        else if (Object.keys(changes).length === 0) toast.success('메인 섹션 노출을 반영했습니다.');
      } finally {
        setSectionsBusy(false);
      }
    }
    if (Object.keys(changes).length === 0) {
      setBulkUpdateOpen(false);
      clearSelection();
      return;
    }
    bulkUpdate.mutate(
      { ...changes, productIds: ids },
      {
        onSuccess: (res) => {
          setBulkUpdateOpen(false);
          clearSelection();
          reportResult(res, 'products.bulk.updateSuccess');
        },
        onError: (error) => {
          toast.error(friendlyMessage(error, t('products.bulkUpdate.error', { defaultValue: '일괄 변경에 실패했습니다.' })));
        },
      },
    );
  };

  return (
    <PageShell
      title={t('products.list.title')}
      actions={
        <div className="flex items-center gap-2">
          {/* QA-374 — 검색 영역과 목록 사이에 있던 [초기화]를 상단으로 옮기고 이름을 바꿨다. */}
          <Button type="button" variant="outline" onClick={resetFilters} data-testid="product-filters-reset">
            {t('products.filters.resetAll', { defaultValue: '검색 조건 초기화' })}
          </Button>
          <ProductColumnSettings />
          <Button
            type="button"
            variant="outline"
            onClick={() => setExcelOpen(true)}
            data-testid="product-excel-button"
          >
            <FileSpreadsheet size={14} aria-hidden="true" />
            {t('products.excel.button', { defaultValue: '엑셀 일괄' })}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!copyTargetId || copy.isPending}
            onClick={() => copyTargetId && handleCopy(copyTargetId)}
          >
            <Copy size={14} aria-hidden="true" />
            {copy.isPending
              ? t('products.copy.copying', '복사 중…')
              : t('products.copy.action', '상품 복사')}
          </Button>
          <Button asChild variant="cta">
            <Link to="/products/new" aria-label={t('products.actions.create')}>
              {t('products.actions.create')}
            </Link>
          </Button>
        </div>
      }
    >
      <ProductTableToolbar />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[var(--density-default-row-h)] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
          }}
        >
          <p>{t('products.list.error')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('products.actions.retry')}
          </Button>
        </div>
      ) : (
        <ProductTable
          items={items}
          sort={sort}
          onSortChange={setSort}
          selectedIds={selectedIds}
          onToggleRow={toggleSelected}
          onToggleAll={setSelected}
          onCopy={handleCopy}
          copyingId={copy.isPending ? (copy.variables ?? null) : null}
          visibleColumns={visibleColumns}
        />
      )}

      <Pagination
        total={totalItems}
        currentPage={page}
        pageSize={size}
        onPageChange={setPage}
        aria-label={t('products.list.paginationLabel')}
      />

      <ProductBulkActionFooter
        selectedCount={selectedIds.length}
        disabled={bulkBusy || bulkUpdate.isPending || copy.isPending}
        onBulkUpdate={() => setBulkUpdateOpen(true)}
        onBulkDelete={() => setConfirmBulkDelete(true)}
        onBulkCopy={() => setConfirmBulkCopy(true)}
        onClearSelection={clearSelection}
      />

      <Dialog open={confirmBulkDelete} onOpenChange={(open) => { if (!open && !bulkBusy) setConfirmBulkDelete(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('products.bulk.deleteTitle', { defaultValue: '선택 상품 삭제' })}
            </DialogTitle>
            <DialogDescription>
              {t('products.bulk.deleteConfirm', {
                count: selectedIds.length,
                defaultValue: `선택한 ${selectedIds.length}건을 삭제(보관)하시겠습니까? 공개 노출이 중단됩니다.`,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm" disabled={bulkBusy}>
                {t('products.bulk.cancel', { defaultValue: '취소' })}
              </Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={bulkBusy}
              onClick={() => void handleBulkDelete()}
            >
              {bulkBusy
                ? t('products.bulk.deleting', { defaultValue: '삭제 중…' })
                : t('products.bulk.delete', { defaultValue: '선택 삭제' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmBulkCopy} onOpenChange={(open) => { if (!open && !bulkBusy) setConfirmBulkCopy(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('products.bulk.copyTitle', { defaultValue: '선택 상품 복사' })}
            </DialogTitle>
            <DialogDescription>
              {t('products.bulk.copyConfirm', {
                count: selectedIds.length,
                defaultValue: `선택한 ${selectedIds.length}건의 복사본을 목록에 생성합니다. 복사본은 미노출 임시저장 상태로 생성되며, 수정은 목록에서 따로 진행합니다.`,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm" disabled={bulkBusy}>
                {t('products.bulk.cancel', { defaultValue: '취소' })}
              </Button>
            </DialogClose>
            <Button
              variant="cta"
              size="sm"
              disabled={bulkBusy}
              onClick={() => void handleBulkCopy()}
            >
              {bulkBusy
                ? t('products.bulk.copying', { defaultValue: '복사 중…' })
                : t('products.bulk.copy', { defaultValue: '선택 복사' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProductBulkUpdateModal
        open={bulkUpdateOpen}
        onOpenChange={setBulkUpdateOpen}
        selectedCount={selectedIds.length}
        busy={bulkUpdate.isPending || sectionsBusy}
        homeSections={activeHomeSections}
        onSubmit={(changes, sectionChanges) => void handleBulkUpdate(changes, sectionChanges)}
      />

      <ProductExcelDialog
        open={excelOpen}
        onClose={() => setExcelOpen(false)}
        listFilters={queryParams}
        importBusy={importExcel.isPending}
        onImport={(file) => importExcel.mutateAsync(file)}
        importFullBusy={importExcelFull.isPending}
        onImportFull={(file) => importExcelFull.mutateAsync(file)}
      />
    </PageShell>
  );
};
